import { useTranslation } from 'tools/i18n';
import { makeStyles } from '@material-ui/styles';
import { Button, LinearProgress } from '@material-ui/core';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { TrackingContext } from '../providers/tracking';
import theme from '../../components/theme';


const useStyles = makeStyles(
	() => ({
		wrapper: {
			display: 'flex',
			justifyContent: 'center',
			margin: '40px 0 20px',
			'&.hidden': {
				display: 'none'
			}
		},
		top: {
			justifyContent: 'center',
			alignItems: 'center'
		},
		bottom: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			marginTop: 15
		},
		text: {
			margin: '0 0 10px',
			textAlign: 'center',
			fontSize: 12,
			color: '#666'
		},
		progress: {
			margin: '0 auto',
			width: 200,
			height: 1
		},
		button: {
			color: 'white',
			margin: '10px auto 20px',
			padding: '14px 26px',
			fontSize: 14,
			minWidth: 250,
			fontWeight: 300,
			letterSpacing: 0.5,
			textTransform: 'none',
			backgroundColor: 'rgb(33, 33, 33)',
			borderRadius: 4,
			fontSmoothing: 'auto',
			'&:hover': {
				backgroundColor: 'rgb(55, 55, 55)'
			},
			'&:disabled': {
				color: 'white'
			},
			'.xmas &': {
				backgroundColor: theme?.palette['xmas'].main || 'rgb(33, 33, 33)',
			}
		}
	}),
	{ name: 'ProductInfiniteScroll' }
);

export default function ProductInfiniteScroll({
	currentPage,
	loadedPages,
	loadedProducts,
	totalProducts,
	productsPerPage,
	changePage,
	position
}) {
	const router = useRouter();
	const { t } = useTranslation();
	const classes = useStyles();
	const totalPages = loadedProducts / totalProducts;
	const { query, asPath } = router;
	const { page, order, slug, initial, ...filters } = query;
	const [isLoading, setIsLoading] = useState(false);
	const { actions: trackingActions } = useContext(TrackingContext);
	const showTop = currentPage > 1 && loadedPages.length > 0 && loadedPages[0] > 1;

	useEffect(() => {
		setIsLoading(false);
	}, [currentPage]);

	function triggerPageChange(page) {
		setIsLoading(true);
		changePage(page);
	}

	function prevLoadedPage() {
		return loadedPages[0] - 1;
	}

	function nextLoadedPage() {
		return loadedPages[loadedPages.length - 1] + 1;
	}

	function getPageLink() {
		let href = {
			pathname: asPath.split('?')[0],
			query: {
				...filters
			}
		};

		if ((position === 'top' ? prevLoadedPage() : nextLoadedPage()) > 1) {
			href.query.page = position === 'top' ? prevLoadedPage() : nextLoadedPage();
		}

		if (order) {
			href.query.order = order;
		}

		return href;
	}

	return (
		<div className={[classes.wrapper, position === 'top' && showTop ? '' : 'hidden']}>
			{position === 'top' ? (
				<div className={classes.top} style={{ display: showTop ? 'flex' : 'none' }}>
					<Link href={getPageLink()} scroll={false}>
						<a
							onClick={event => {
								event.preventDefault();
								event.stopPropagation();

								trackingActions.click({
									type: 'category_load_more',
									text: t('show-previous'),
									identifier: getPageLink().pathname
								});
								triggerPageChange(prevLoadedPage());
							}}
							style={{ color: 'inherit', textDecoration: 'none' }}
						>
							<Button className={classes.button}>{t('show-previous')}</Button>
						</a>
					</Link>
				</div>
			) : (
				<div
					className={classes.bottom}
					style={{
						display: loadedPages[loadedPages.length - 1] * totalPages < totalProducts ? 'flex' : 'none'
					}}
				>
					<span className={classes.text}>
						{t('showing-of', { products: Math.min(productsPerPage * (page || 1), totalProducts), total: totalProducts })}
					</span>
					<LinearProgress
						className={classes.progress}
						variant="determinate"
						value={(loadedProducts / totalProducts) * 100}
					/>
					{Math.max.apply(null, loadedPages) < totalProducts / productsPerPage ? (
						<Link href={getPageLink()} scroll={false}>
							<a
								onClick={event => {
									event.preventDefault();
									event.stopPropagation();

									trackingActions.click({
										type: 'category_load_more',
										text: t('show-more-pagination', { productsPerPage: productsPerPage }),
										identifier: getPageLink().pathname
									});
									triggerPageChange(nextLoadedPage());
								}}
								style={{ color: 'inherit', textDecoration: 'none' }}
							>
								<Button className={classes.button} disabled={isLoading}>
									{t('show-more-pagination', {
										productsPerPage: Math.min(totalProducts - loadedProducts, productsPerPage)
									})}
								</Button>
							</a>
						</Link>
					) : (
						''
					)}
				</div>
			)}
		</div>
	);
}
